/**
 * Bookings — the one place to find any booking (R-2812-b, board 2812 M3): labelled
 * Who / When filter rows and Find in the title row, over the shared list (`List`,
 * `Row`), by journey date under day headings. Rows open the one detail screen.
 * Failed reads render error copy, never an empty-list lie.
 *
 * ⭐⭐ BOARD 2812 M3 — THE STATUS CHIPS RETIRED. The screen reads M1's filtered
 * `GET /api/bookings?who&when&home` (a Find term POSTs to `/api/bookings/find`, board
 * 2812 M4: never in a URL), so Who (All, Web, All homes, or one home
 * from "Pick a home"), When (From today, Tomorrow, History) and Find decide the
 * list on the SERVER; the screen never re-derives a pile or a date rule. Tomorrow
 * is the dispatch view (Helen named it): every leg in time order, the return leg
 * its own row, and tomorrow's unconfirmed home requests placed among them with
 * where they wait. ⭐ board 3021 (Alex's ruling on question 1386): From today lists
 * the same way, legs and waiting requests, from today on. One chip per row, the
 * money folded in (`booking-chip-words.ts`).
 * `BookingsList` is the shared list the Web enquiries' Bookings tab reuses with
 * Who fixed to Web.
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Chip, EmptyState, FilterRow, List, Row, toneFor } from '@ecmb/ui';
import {
  listBookings,
  listBookingsFiltered,
  listHomeGroups,
  type ApiBookingRow,
  type ApiFilteredBookingRow,
  type ApiHomeGroup,
  type ApiWaitingRequest,
} from '../lib/actions.js';
import { bookingTypeLabel, type Registry } from '../store/registry.js';
import { civilDateLabel, euro, relativeAge } from '../lib/format.js';
// ⛔ THE ONE RENDER OF A CAPTURED HEADCOUNT (T1871 M6). Never a local
// truthiness test: `Number(null) === 0`, so "not captured" would print as a
// party of nobody. This function is where that rule lives.
import { formatPassengerCount } from '../../shared/passengers.js';
import { chipWords, noteOf, requestActionChip } from '../../shared/booking-chip-words.js';
import { dublinWallClockOf } from '../../shared/needs-helen-now.js';
import { formatDublinInstant } from '../../shared/dublin-date.js';
import { Banner } from '../components/Banner.js';
import { ApiError } from '../lib/api.js';

/** T408: background freshness tick for the list — DB-only read. */
const LIST_REFRESH_MS = 15_000;

/**
 * T1011 M2 — THE ONE PLACE A ROW'S DISPLAY NAME IS DECIDED, and it exists
 * because migration v33 made `bookings.customer_id` nullable: an ACCOUNT booking
 * is billed to an organisation and has no customer row, so `customer_name`
 * arrives as `null`.
 *
 * ⛔ THE FALLBACK IS THE BOOKING'S OWN NUMBER, NOT A FIXED PHRASE, and that is
 * an accessibility decision rather than a stylistic one. This string is the
 * row button's `aria-label`, so a null would leave the control with an EMPTY
 * ACCESSIBLE NAME — announced as "button", indistinguishable from every other
 * row. A shared phrase such as "Account booking" would be worse still: several
 * rows would share one accessible name, which is the collision `aria-label`
 * exists to avoid. The id is present, unique per row, and already what the
 * operator quotes on the phone.
 */
export function bookingRowLabel(
  r: Pick<
    ApiBookingRow,
    'booking_id' | 'customer_name' | 'account_name' | 'resident_display_name' | 'job_ref'
  >,
): string {
  /**
   * ⭐⭐ T1746 M4 (B5, DD6) - AN ACCOUNT BOOKING IS NAMED BY ITS HOME, BY THE
   * RESIDENT WHEN THE PORTAL COULD SAY WHO, AND ALWAYS BY ITS OWN REFERENCE.
   * Until now it read `Booking 41` on a screen where every other row carries a
   * person's name.
   *
   * ⛔⛔ THE REFERENCE IS NOT DECORATION, IT IS THE PER-ROW DISAMBIGUATOR, and
   * the docblock above states why this label must carry one: the string is the
   * row button's `aria-label`, so several rows sharing one accessible name is
   * precisely the collision `aria-label` exists to avoid. A home's name is NOT
   * per-row, and the collision is ROUTINE rather than rare:
   * `insertOccurrenceBooking` (core/data/occurrences-repo.ts) writes no
   * `inquiries` row, so a generated occurrence never resolves a resident and
   * every occurrence of one home's series would announce the identical name on
   * Helen's main list. Naming the resident only moves the collision one step
   * along: one resident, two dates, one name. `job_ref` carries UNIQUE
   * (`bookings_job_ref_key`) and the booking id is unique, so an account label
   * is unique BY CONSTRUCTION rather than by luck.
   *
   * ⚠️ AND IT IS THE HONEST THING TO SHOW: the reference is what the home's
   * invoice prints and what the nurse holds, so it names the run to both sides
   * of the call. A resident's name is a person, never a per-row identity.
   *
   * ⛔ FOUR CASES, IN THIS ORDER, AND THE ORDER IS THE DECISION:
   *   home + resident - `Kilcoole Lodge · Mary N · SCNH 05`, the crossed run.
   *   home alone      - `Kilcoole Lodge · SCNH 07`: a hand-keyed account
   *                     booking, or a crossed one whose name the seam could not
   *                     be asked for. ⛔ The SAME rendering for both,
   *                     deliberately: a degraded seam must not be
   *                     distinguishable from a booking that never had a
   *                     resident, because the screen knows nothing that would
   *                     let it tell the difference honestly.
   *   customer        - every consumer booking, byte-identical to before.
   *   neither         - `Booking N`, the existing per-row fallback.
   *
   * ⚠️ `job_ref` IS NULL ON EVERY BOOKING WRITTEN BEFORE T1683 M3 and there is
   * no backfill, by ruling, so the two account cases fall back to `Booking N`
   * for the reference limb: the same per-row unique handle the `neither` case
   * already uses. An empty string is treated as an absence for the same reason a
   * null is, because it names nothing.
   *
   * ⛔ TEXT, NEVER A CHIP. This string is the row button's `aria-label`, and the
   * per-row `.chip` count is net-locked (`bookings-paid.spec.tsx`); a chip here
   * would also put a resident's name in a coloured badge on a list Helen scans.
   * ⚠️ The separator is the kiosk's ` · ` (U+00B7), the one this row's meta line
   * already joins with.
   */
  const parts = bookingRowLabelParts(r);
  return parts.handle === null ? parts.lead : `${parts.lead} · ${parts.handle}`;
}

/**
 * ⭐⭐ T1749 M8 round 5 (output gate, BLOCK) — THE SAME LABEL, IN ITS PARTS, SO THE
 * RENDERED ROW CAN KEEP THE REFERENCE WHOLE.
 *
 * `bookingRowLabel` above is the STRING: the row button's `aria-label`, the
 * paid-notice's list, and every text assertion. This is the same label as
 * `{ lead, handle }`, and the string is composed FROM it, so the two cannot
 * disagree about what a row is called.
 *
 * ⛔⛔ WHY IT EXISTS: A HYPHEN IS A LINE-BREAK OPPORTUNITY (UAX #14), so plain
 * `word-break: normal` DOES split `ECM-FyBr-sLsT`. Measured live at 950 with the
 * whole label in one text node: a `Range` over the reference returned TWO client
 * rects (y 656 then y 686) — the row read `…Home 123456 · ECM-FyBr-` / `sLsT`.
 * Removing `overflow-wrap: break-word` (round 3) never closed that, because
 * `break-word` only adds breaks INSIDE a token with no opportunity of its own; a
 * hyphenated reference has three. The one string a phone call is built around was
 * the one string on this row that could break, and `bug-h`'s two existing
 * assertions could not see it (`toContainText` reads DOM text, which wrapping
 * never changes, and the strip's line count bounds `.booking-row-meta`, a
 * different element).
 * ⇒ the reference renders in its own span wearing `.job-ref-value`
 * (`white-space: nowrap`, `app.css`) — the class four other surfaces already use
 * for exactly this, and the fifth surface is named in its docblock. Graded at
 * 1024 AND 950 by the rects arm in `bug-h-booking-row-clip.spec.ts`.
 * ⚠️ THE RENDERED TEXT IS BYTE-IDENTICAL: the same lead, the same ` · `, the same
 * handle, split across two nodes that `textContent` re-joins. Nothing in the net
 * moves.
 * ⭐ board 2812 M3 fix round 3 (F3-5): the parts are joined ONLY when present (an
 * empty resident name is absent). `names` is the lead's present parts, for the RENDERED
 * row, which joins them (and the handle) with `WHO_SEP`, whose two no-break spaces glue
 * the dot to both neighbours: a narrow who column wraps INSIDE a part, so no line ends
 * "Seat Home B ·" and none starts "· Booking 652". `lead` stays the plain ` · ` string
 * `bookingRowLabel` composes (the label's own words are unchanged).
 */
export function bookingRowLabelParts(
  r: Pick<
    ApiBookingRow,
    'booking_id' | 'customer_name' | 'account_name' | 'resident_display_name' | 'job_ref'
  >,
): { lead: string; names: string[]; handle: string | null } {
  if (r.account_name != null) {
    // ⚠️ READ INTO A LOCAL AND NARROWED BY `typeof`, never compared in place -
    // the same treatment, for the same reason, as the row mapper in
    // `listBookings` (core/data/bookings-repo.ts).
    const ref = r.job_ref;
    const handle = typeof ref === 'string' && ref !== '' ? ref : `Booking ${r.booking_id}`;
    const names = [r.account_name, r.resident_display_name].filter((p): p is string => (p ?? '') !== '');
    return { lead: names.join(' · '), names, handle };
  }
  // ⛔ A CUSTOMER ROW HAS NO HANDLE LIMB, and the `Booking N` fallback is the
  // LEAD here rather than the handle: it is the whole name, and wrapping it in
  // the no-wrap span would be a claim that it is a reference, which it is not.
  const lead = r.customer_name ?? `Booking ${r.booking_id}`;
  return { lead, names: [lead], handle: null };
}

/** ⭐ board 2812 M3 fix round 3 (F3-5): the who line's separator, a middle dot with a
 *  NO-BREAK SPACE on each side, so it never ends or starts a wrapped line. */
export const WHO_SEP = '\u00a0\u00b7\u00a0';

/** ⭐ board 2812 M4 (plan step 4): on a home's page the rows drop the home's name (the
 *  page names it once); a customer row has no home limb and is unchanged. */
export function withoutHome(parts: { lead: string; names: string[]; handle: string | null }): { lead: string; names: string[]; handle: string | null } {
  if (parts.handle === null) return parts;
  const names = parts.names.slice(1);
  return { ...parts, names, lead: names.join(' · ') };
}

/** The who cell: the names, then the reference in its own no-wrap span. */
export function whoCell(parts: { lead: string; names: string[]; handle: string | null }): ComponentChildren {
  if (parts.handle === null) return parts.lead;
  return (
    <>
      {parts.names.join(WHO_SEP)}
      {/* ⛔ THE REFERENCE NEVER BREAKS (T1749 M8 round 5): its own no-wrap span. */}
      {parts.names.length > 0 ? WHO_SEP : null}<span class="job-ref-value">{parts.handle}</span>
    </>
  );
}

/** Who the list shows: all, web, all homes, or one home by its group key (Q240). */
export type BookingsWho = 'all' | 'web' | 'homes' | { home: string };
export type BookingsWhen = 'from-today' | 'tomorrow' | 'history';

/** Where a screen asks the list to land (Done's Back to Bookings asks for this).
 *  ⭐ board 2812 M5 (fix round 1, W2): and where Back from a booking returns it, so
 *  `who` may be a picked home. */
export type BookingsLanding = { who: BookingsWho; when: BookingsWhen };

/**
 * ⭐ Board 2812 M3 (plan step 7) - DONE'S "Back to Bookings" LANDS HERE: Who Web,
 * When From today. The status chips it used to open (`new`) retired with the
 * filter rows, and a web job Helen just saved is a web booking from today on.
 */
export const BACK_TO_BOOKINGS: BookingsLanding = { who: 'web', when: 'from-today' };

/** The landing for every other entry (the rail, Back, the detail's fallback). */
export const BOOKINGS_DEFAULT: BookingsLanding = { who: 'all', when: 'from-today' };

/** Find's debounce: one read per pause in typing, never one per keystroke. */
export const FIND_DEBOUNCE_MS = 250;

const WHO_CHIPS: ReadonlyArray<{ who: 'all' | 'web' | 'homes'; label: string }> = [
  { who: 'all', label: 'All' },
  { who: 'web', label: 'Web' },
  { who: 'homes', label: 'All homes' },
];
/** The When chips' words, one table (WT-3's empty state names the When it searched). */
const WHEN_WORDS: Readonly<Record<BookingsWhen, string>> = {
  'from-today': 'From today',
  tomorrow: 'Tomorrow',
  history: 'History',
};
const WHEN_CHIPS: ReadonlyArray<{ when: BookingsWhen; label: string }> = (
  ['from-today', 'tomorrow', 'history'] as const
).map((w) => ({ when: w, label: WHEN_WORDS[w] }));

/** The words the list says about itself (R-11: listed for Vicky). */
export const BOOKINGS_WORDS = {
  /** ⭐ board 3020 /check (Captain's ruling): the chip already names the step, so the row
   *  says only the state; the longer sentence wrapped a waiting row to three lines at 1024. */
  waiting: 'Not confirmed yet',
  waitingUnread: 'Nursing home requests could not be read just now, so unconfirmed trips are not shown.',
  residentsUnread: 'Resident names could not be read just now',
  truncated: 'Showing the first 200. Find narrows the list.',
  noReply: 'No reply',
  noDate: 'No date yet',
  /** ⭐ board 2812 M4 fix round (C10): a row under a DATED day heading with no time. */
  noTime: 'No time yet',
  pickAHome: 'Pick a home',
} as const;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
    kind: 'ready';
    rows: ApiFilteredBookingRow[];
    truncated: boolean;
    residentsUnread: boolean;
    waiting: ApiWaitingRequest[] | null | undefined;
  };

/** The row's tone: the status word's (R1); none for a status the registry cannot tone. */
function toneOfRow(r: ApiFilteredBookingRow) {
  const { toneStatus } = chipWords(r);
  return toneStatus === null ? undefined : toneFor('booking', toneStatus);
}

/** The time a row is placed at: the out leg's journey time, or the back leg's
 *  Dublin wall clock from `return_at`. `null` = no time (sorted last by the server). */
function legTime(r: ApiFilteredBookingRow): string | null {
  if (r.leg === 'back') return dublinWallClockOf(r.return_at)?.time ?? null;
  return r.journey_time;
}

/** A where-line part: a plain fact, or the row's one note (`noteOf`). */
export type WherePart = string | { words: string; flag: boolean } | null | false | undefined;

/**
 * ⭐⭐ board 2812 M4 (the 2811 merge) - A ROW'S WHERE LINE WITH ITS ONE NOTE. The parts
 * joined by " · "; a balance FLAG's words (`noteOf`'s `flag`) sit in a `.row-flag`
 * span painted gold-700, the "needs a human" text colour (board 2811 rework part 2c,
 * Captain item 14), with no box: the row keeps ONE chip. The To do cards and Need an
 * outcome build their line here too, so every list says it alike.
 */
export function whereLine(parts: readonly WherePart[]): ComponentChildren {
  const kept = parts.filter((p): p is string | { words: string; flag: boolean } => Boolean(p));
  if (!kept.some((p) => typeof p !== 'string' && p.flag)) {
    return kept.map((p) => (typeof p === 'string' ? p : p.words)).join(' · ');
  }
  return kept.flatMap((p, i) => [
    i > 0 ? ' · ' : '',
    typeof p === 'string' ? p : p.flag ? <span key="flag" class="row-flag">{p.words}</span> : p.words,
  ]);
}

/** The route, the service, the access need and the one note, on the row's where line.
 *  ⭐ board 2812 M4: the note is `noteOf`'s (a balance flag, then a taxi hand-off), and
 *  "No reply" only after both, so a flag never hides behind it. */
function whereOf(registry: Registry, r: ApiFilteredBookingRow): ComponentChildren {
  const route = r.leg === 'back'
    ? (r.destination !== null ? `Back from ${r.destination}` : 'Back')
    : [r.pickup, r.destination].filter(Boolean).join(' to ');
  const note = noteOf(r) ?? (r.no_reply ? BOOKINGS_WORDS.noReply : null);
  return whereLine([
    // FB7: a nursing-home quote folds to booking_type 'local-other' (reads Custom),
    // so the SERVER's latest quote service names it instead.
    r.money_kind === 'web'
      ? (r.latest_quote_service === 'nursing-home' ? 'Nursing home' : bookingTypeLabel(registry, r.booking_type))
      : null,
    route || null,
    r.accessible_vehicle ? 'Accessible vehicle' : null,
    note,
    // T409 hygiene: an unquoted (parked/new) booking shows its AGE, so a stale park
    // is visible at a glance; LAST on the line, a freshness note, not a fact.
    r.status === 'new' && r.created_at && relativeAge(r.created_at) ? `Saved ${relativeAge(r.created_at)}` : null,
  ]);
}

/**
 * ⭐ T1871 M6 (board 2161's order), on the shared `Row`'s money slot: the band, the
 * exact headcount BESIDE it, then the price. `formatPassengerCount` answers null for
 * "not captured" (and for 0), so those rows read the band alone, with no gap and no
 * 0. ⛔ T1871 M3: the count and the money figure never meet on one source line
 * (`test/shared/passengers.spec.ts` reads it line by line), and the To do's cards
 * call this rather than carrying the count themselves.
 */
export function rowMoneySlot(row: ApiFilteredBookingRow): string {
  const count = formatPassengerCount(row.passengers_exact);
  const price = row.latest_quote_cents != null ? euro(row.latest_quote_cents) : null;
  return [row.passengers_band, count, price].filter(Boolean).join(' · ');
}

/** One booking row on the shared `Row`: it opens the booking's own screen. */
function BookingRow({
  registry,
  row,
  showDay,
  onOpen,
  dropHome = false,
  underDay = false,
}: {
  registry: Registry;
  row: ApiFilteredBookingRow;
  /** A To do card or an undated heading has no day above it, so the row says it. */
  showDay: boolean;
  onOpen: (bookingId: number) => void;
  /** ⭐ board 2812 M4: on a home's page the row drops the home's name. */
  dropHome?: boolean;
  /** ⭐ board 2812 M4 fix round (C10): the row sits under a DATED day heading. */
  underDay?: boolean;
}) {
  const parts = dropHome ? withoutHome(bookingRowLabelParts(row)) : bookingRowLabelParts(row);
  const time = legTime(row);
  // ⭐ board 2812 M3 fix round 2 (UX-7): never an empty when cell. A row with no date and
  // no time says so in the heading's own words. ⭐ board 2812 M4 fix round (C10): under a
  // DATED heading the day is known and only the time is missing, so it says "No time yet".
  const when = showDay && row.journey_date !== null
    ? civilDateLabel(row.journey_date, time)
    : (time ?? (underDay ? BOOKINGS_WORDS.noTime : BOOKINGS_WORDS.noDate));
  const tone = toneOfRow(row);
  const money = rowMoneySlot(row);
  return (
    <Row
      when={when}
      who={whoCell(parts)}
      where={whereOf(registry, row)}
      money={money === '' ? undefined : money}
      chip={<Chip neutral tone={tone}>{chipWords(row).words}</Chip>}
      onOpen={() => {
        onOpen(row.booking_id);
      }}
    />
  );
}

/** One of the waiting requests (M1's `waiting`, on From today and Tomorrow, board 3021):
 *  placed in day and time order, it says where it waits and opens there. It carries no
 *  booking and no home key. */
function WaitingRow({ req, onOpen, dropHome = false, underDay = false }: {
  req: ApiWaitingRequest;
  onOpen: (requestId: number) => void;
  /** ⭐ board 2812 M4 fix round: on a home's page the row drops the home's name, as a
   *  booking row does. */
  dropHome?: boolean;
  /** ⭐ board 3020 /check (Low): the row sits under a DATED day heading, as `BookingRow`'s. */
  underDay?: boolean;
}) {
  const at = dublinWallClockOf(req.leadAt ?? req.pickupAt);
  /* ⭐⭐ board 2812 M3 fix round 2 (UX-5): a waiting request IS a Need you now row, so its
     chip names Helen's next step like every one (`requestActionChip`). ⭐ board 3020: from
     the SERVER's `needsHelenNow` primary (`req.primary`), the one table the Requests card
     reads, never a guess from the status here: its ask's answer, "Price this" for a new
     request, "Confirm to book" for a priced one, "Check this request" when no step is
     named. Neutral, never a tone. */
  const word = requestActionChip(req.primary ?? null, req);
  /* ⭐ board 3020 /check (Captain's ruling): a request is not a booking, so a waiting RETURN
     gets no leg row of its own; its where line says it comes back, in its Nursing homes
     card's words ("(and back)", then "Back: ..." when the time is known). Behind the return
     switch, as the card's `backText` is. On the lead's own Dublin day the clock alone (the
     row already sits under that day); on another day the card's full instant. */
  const back = req.returnTrip ? dublinWallClockOf(req.returnAt) : null;
  const backWords = back === null
    ? null
    : at !== null && back.day === at.day ? back.time : formatDublinInstant(req.returnAt);
  const destination = req.destinationName ? `to ${req.destinationName}` : null;
  return (
    <Row
      /* ⭐ board 3020 /check (Low): never an empty when cell, by `BookingRow`'s own rule. */
      when={at?.time ?? (underDay ? BOOKINGS_WORDS.noTime : BOOKINGS_WORDS.noDate)}
      who={[dropHome ? null : req.homeName, req.residentDisplayName].filter(Boolean).join(' · ')}
      where={[
        req.returnTrip ? [destination, '(and back)'].filter(Boolean).join(' ') : destination,
        backWords !== null ? `Back: ${backWords}` : null,
        req.accessibleVehicle ? 'Accessible vehicle' : null,
        BOOKINGS_WORDS.waiting,
      ].filter(Boolean).join(' · ')}
      money={req.pricedAmountCents != null ? euro(req.pricedAmountCents) : undefined}
      chip={<Chip neutral>{word}</Chip>}
      onOpen={() => {
        onOpen(req.requestId);
      }}
    />
  );
}

/** A list entry: a booking leg, or (From today and Tomorrow) a waiting request, with the
 *  day and time it sorts on. */
type Entry =
  | { kind: 'booking'; row: ApiFilteredBookingRow; key: string; day: string | null; time: string | null }
  | { kind: 'waiting'; req: ApiWaitingRequest; key: string; day: string | null; time: string | null };

const VALID_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * The waiting requests (From today and Tomorrow) placed among the legs by (day, time),
 * as `compareByJourney` orders the legs (board 3021, D10). ⛔ The server ordered the legs
 * (a malformed or missing time last within a day, an undated leg last); this only slots
 * each waiting row in before the first entry that is undated, on a later day, or on its
 * day with a later time or a missing or malformed one, so the legs keep the server's
 * order. A waiting row with no time goes after its day's entries; an undated one last.
 */
function mergeWaiting(legs: Entry[], waiting: readonly ApiWaitingRequest[]): Entry[] {
  const out = [...legs];
  for (const req of waiting) {
    const at = dublinWallClockOf(req.leadAt ?? req.pickupAt);
    const day = at?.day ?? null;
    const time = at?.time ?? null;
    const entry: Entry = { kind: 'waiting', req, key: `w${String(req.requestId)}`, day, time };
    const idx = day === null ? -1 : out.findIndex((e) => e.day === null
      || e.day > day
      || (e.day === day && time !== null && (e.time === null || !VALID_TIME.test(e.time) || e.time > time)));
    if (idx < 0) out.push(entry);
    else out.splice(idx, 0, entry);
  }
  return out;
}

/** Group the entries under day headings, keeping their order (the server's). */
function byDay(entries: Entry[]): Array<{ day: string | null; entries: Entry[] }> {
  const groups: Array<{ day: string | null; entries: Entry[] }> = [];
  for (const e of entries) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.day === e.day) last.entries.push(e);
    else groups.push({ day: e.day, entries: [e] });
  }
  return groups;
}

export type BookingsListProps = {
  registry: Registry;
  /** ⭐ board 2812 M5 (fix round 1, W2): with the Who and When the list showed when the
   *  row was opened, so a host can land Back from the booking on them. */
  onOpen: (bookingId: number, at: BookingsLanding) => void;
  /** Unique on the screen: the filter rows' ids derive from it. */
  idPrefix: string;
  /** Fixed by a host: no Who row then. Web enquiries' Bookings tab: `web`; ⭐ board 2812
   *  M4: All homes' Bookings `homes`, and a home's Bookings that home (`{ home }`). */
  fixedWho?: 'web' | 'homes' | { home: string };
  /** ⭐ board 2812 M4: the homes column sits beside the list (the Row's layout). */
  beside?: boolean;
  /** ⭐ board 2812 M4: on a home's page the rows drop the home's name. */
  dropHome?: boolean;
  /** ⭐ board 2812 M4: a host's write (a phoned-in trip) asks for a fresh read. */
  refreshKey?: number;
  /** ⭐ board 2812 M4 (step 2): the home's key is unknown to the server (404), so the
   *  host falls back to All homes. */
  onStale?: () => void;
  landing?: BookingsLanding;
  /** The debounced Find term ('' when none). */
  find?: string;
  /** A waiting row (From today, Tomorrow): open Nursing homes' To do with that card open. */
  onOpenWaiting?: (requestId: number) => void;
};

/**
 * ⭐⭐ THE SHARED LIST (board 2812 M3): the When row (and the Who row unless a host
 * fixes Who), then the rows by journey date under day headings.
 *
 * ⛔ T1155 — THE DEPOSIT-PAID WATCH READS ALL THE MONEY, NEVER THE CURRENT FILTER.
 * The seed is ONE unfiltered read (`listBookings(null)`, the legacy route)
 * taken beside the display read, so "paid and not in the seed" means new money in
 * EVERY window, a filtered one included. ⛔ null means NOT YET SEEDED, and nothing
 * announces while it is null. The set only grows, so no ordering (a stale read, a
 * 401, a filter change) can lose or replay an announcement.
 */
export function BookingsList({
  registry, onOpen, idPrefix, fixedWho, landing, find = '', onOpenWaiting, beside, dropHome, refreshKey = 0, onStale,
}: BookingsListProps) {
  const start = landing ?? BOOKINGS_DEFAULT;
  const [who, setWho] = useState<BookingsWho>(fixedWho ?? start.who);
  const [when, setWhen] = useState<BookingsWhen>(start.when);
  /** ⭐ board 2812 M5 (fix round 1, W2): a row opens with the filter it was found under. */
  const openAt = (bookingId: number) => onOpen(bookingId, { who, when });
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [homes, setHomes] = useState<ApiHomeGroup[] | null>(null);
  const latest = useRef(0);
  const paidSeen = useRef<Set<number> | null>(null);
  const [justPaid, setJustPaid] = useState<Array<{ booking_id: number; label: string }>>([]);

  const isPaid = (r: Pick<ApiBookingRow, 'payment_status'>) => r.payment_status === 'deposit_paid' || r.payment_status === 'paid';

  const noticeNewlyPaid = (rows: readonly ApiFilteredBookingRow[]) => {
    const seen = paidSeen.current;
    if (seen === null) return;
    const paidNow = rows.filter(isPaid);
    // ⭐ board 3021 (D8): both legs of one paid return trip arrive in one read, so the
    // booking is named once (the first leg wins).
    const counted = new Set<number>();
    const fresh = paidNow.filter((r) => {
      if (seen.has(r.booking_id) || counted.has(r.booking_id)) return false;
      counted.add(r.booking_id);
      return true;
    });
    for (const r of paidNow) seen.add(r.booking_id);
    if (fresh.length > 0) {
      const named = fresh.map((r) => ({ booking_id: r.booking_id, label: bookingRowLabel(r) }));
      setJustPaid((prev) => [...named, ...prev.filter((p) => !named.some((f) => f.booking_id === p.booking_id))]);
    }
  };

  const seedPaidWatch = () => {
    listBookings(null)
      .then((rows) => {
        if (paidSeen.current === null) paidSeen.current = new Set(rows.filter(isPaid).map((r) => r.booking_id));
      })
      .catch(() => {
        /* stay null; the next good read retries. Never seed from a filtered window. */
      });
  };

  const read = (w: BookingsWho, n: BookingsWhen, term: string) => listBookingsFiltered({
    who: typeof w === 'string' ? w : 'homes',
    when: n,
    ...(typeof w === 'string' ? {} : { home: w.home }),
    ...(term.trim() === '' ? {} : { q: term }),
  });

  const load = (w: BookingsWho, n: BookingsWhen, term: string, quiet: boolean) => {
    const seq = ++latest.current;
    if (!quiet) setState({ kind: 'loading' });
    return read(w, n, term)
      .then((res) => {
        if (paidSeen.current === null) seedPaidWatch();
        if (seq !== latest.current) return;
        noticeNewlyPaid(res.rows);
        setState({
          kind: 'ready',
          rows: res.rows,
          truncated: res.truncated,
          residentsUnread: res.residentSearch === 'unavailable',
          waiting: res.waitingRead === 'failed' ? null : res.waiting,
        });
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404 && onStale !== undefined) {
          onStale();
          return;
        }
        if (!quiet && seq === latest.current) setState({ kind: 'error' });
        throw e;
      });
  };

  useEffect(() => {
    seedPaidWatch();
    // "Pick a home" lists the homes from the server read (M1's `/api/home-groups`).
    // A failed read leaves the picker out rather than offering an empty one.
    if (fixedWho === undefined) {
      listHomeGroups().then((g) => setHomes(Array.isArray(g.groups) ? g.groups : null)).catch(() => setHomes(null));
    }
  }, []);

  // A filter change or a new Find term reads afresh; the poll below refreshes it.
  useEffect(() => {
    load(who, when, find, false).catch(() => undefined);
  }, [typeof who === 'string' ? who : who.home, when, find, refreshKey]);

  // T408: silent background refresh (a paid deposit surfaces while the list is
  // simply left open). Success replaces the rows; a transient failure keeps the
  // current render; an AUTH failure (401/429) stops THIS interval.
  useEffect(() => {
    const id = setInterval(() => {
      load(who, when, find, true).catch((e: unknown) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 429)) clearInterval(id);
      });
    }, LIST_REFRESH_MS);
    return () => clearInterval(id);
  }, [typeof who === 'string' ? who : who.home, when, find]);

  const pickedHome = typeof who === 'string' ? '' : who.home;
  const entries: Entry[] = state.kind === 'ready'
    ? state.rows.map((row, i) => ({
      kind: 'booking' as const,
      row,
      key: `${String(row.booking_id)}-${row.leg ?? 'r'}-${String(i)}`,
      /* ⭐ board 2812 M3 fix round 2 (UX-9): History groups by the day the server SORTED it
         by (the day the job ended: a No reply quote's no-reply day, not its trip day), so
         the headings run newest first and never jump back and forth. */
      day: row.history_day !== undefined
        ? row.history_day
        : row.leg === 'back' ? (dublinWallClockOf(row.return_at)?.day ?? row.journey_date) : row.journey_date,
      time: legTime(row),
    }))
    : [];
  const shown = state.kind === 'ready' && when !== 'history' && state.waiting
    ? mergeWaiting(entries, state.waiting)
    : entries;

  return (
    <>
      <div class="filter-rows">
      {fixedWho === undefined && (
        <FilterRow id={`${idPrefix}-who`} label="Who">
          {WHO_CHIPS.map((c) => (
            <Chip key={c.who} shape="filter" pressed={who === c.who} onClick={() => setWho(c.who)}>
              {c.label}
            </Chip>
          ))}
          {homes !== null && homes.length > 0 && (
            /* ⛔ A NATIVE SELECT, NEVER A CHIP PER HOME (R-2812-b): its values are the
               group keys (a short code or `a<id>`, Q240), never a portal home id. */
            <select
              class="login-input bookings-pick"
              aria-label={BOOKINGS_WORDS.pickAHome}
              value={pickedHome}
              onChange={(e) => {
                const key = (e.target as HTMLSelectElement).value;
                setWho(key === '' ? 'all' : { home: key });
              }}
            >
              <option value="">{BOOKINGS_WORDS.pickAHome}</option>
              {homes.map((h) => <option key={h.key} value={h.key}>{h.name}</option>)}
            </select>
          )}
        </FilterRow>
      )}
      <FilterRow id={`${idPrefix}-when`} label="When">
        {WHEN_CHIPS.map((c) => (
          <Chip key={c.when} shape="filter" pressed={when === c.when} onClick={() => setWhen(c.when)}>
            {c.label}
          </Chip>
        ))}
      </FilterRow>
      </div>

      {justPaid.length > 0 && (
        // Copy is deliberately NOT 'Deposit paid:' (deposit-paid-visible.spec.ts
        // asserts that string on the detail banner and ABSENT elsewhere).
        <Banner kind="ok">
          Payment received: {justPaid.map((p) => p.label).join(', ')}.{' '}
          <button
            type="button"
            class="btn btn--quiet btn--inline"
            onClick={() => {
              const first = justPaid[0];
              setJustPaid([]);
              if (first) openAt(first.booking_id);
            }}
          >
            Open the booking
          </button>{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={() => setJustPaid([])}>
            Dismiss
          </button>
        </Banner>
      )}

      {state.kind === 'loading' && <EmptyState state="loading" headline="Getting the bookings…" />}
      {state.kind === 'error' && (
        <Banner kind="error">
          Loading the bookings failed. They are safe on the server. Try again in a moment.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={() => { load(who, when, find, false).catch(() => undefined); }}>
            Try again
          </button>
        </Banner>
      )}
      {state.kind === 'ready' && state.residentsUnread && (
        <p class="helper-note">{BOOKINGS_WORDS.residentsUnread}, so a resident cannot be found by name.</p>
      )}
      {state.kind === 'ready' && when !== 'history' && state.waiting === null && (
        <p class="helper-note">{BOOKINGS_WORDS.waitingUnread}</p>
      )}
      {state.kind === 'ready' && shown.length === 0 && (
        find.trim() !== '' ? (
          /* ⭐ board 2812 M3 fix round 2 (WT-3): it says WHICH When it searched. */
          <EmptyState headline={`Nothing matches “${find.trim()}” in ${WHEN_WORDS[when]}.`} evidence="Try a surname, a phone number or a reference." />
        ) : when === 'tomorrow' ? (
          <EmptyState headline="Nothing booked for tomorrow." evidence="Runs for tomorrow land here as they are booked." />
        ) : when === 'history' ? (
          <EmptyState headline="Nothing in the last 30 days." evidence="Find searches every booking, however old." />
        ) : (
          <EmptyState headline="No bookings from today." evidence="Quoted jobs land here as they are saved." />
        )
      )}
      {state.kind === 'ready' && byDay(shown).map((g) => (
        <section key={g.day ?? 'undated'} class="day-group">
          <h2 class="day-heading">{g.day === null ? BOOKINGS_WORDS.noDate : civilDateLabel(g.day, null)}</h2>
          <List label={g.day === null ? BOOKINGS_WORDS.noDate : civilDateLabel(g.day, null)} beside={beside}>
            {g.entries.map((e) => (e.kind === 'booking'
              ? <BookingRow key={e.key} registry={registry} row={e.row} showDay={false} onOpen={openAt} dropHome={dropHome} underDay={g.day !== null} />
              : <WaitingRow key={e.key} req={e.req} onOpen={(id) => onOpenWaiting?.(id)} dropHome={dropHome} underDay={g.day !== null} />))}
          </List>
        </section>
      ))}
      {state.kind === 'ready' && state.truncated && <p class="helper-note">{BOOKINGS_WORDS.truncated}</p>}
      {/* ⭐ board 2812 M4 fix round (L6): Find SAYS what it found, politely, to a screen
          reader: the region is always in the DOM (a live region that appears with its
          words is often not announced) and holds words only while a term is set. The
          library's own `.visually-hidden` (`components.css`), as `Location.tsx` uses it:
          no new class. ⚠️ An EMPTY result says nothing here: the empty state above is
          already a `role="status"` region with its own sentence, so one announcement.
          ⭐ board 2812 fix round 2: it counts what is SHOWN, so a Find on Tomorrow that
          matches only a waiting request says so too (`shown`, never `entries` alone).
          ⭐ board 3021 (D7): it counts BOOKINGS, the distinct booking ids among the shown
          rows, so a return trip shown as an out and a back leg is "1 booking". */}
      <p class="visually-hidden" role="status" aria-live="polite">
        {state.kind === 'ready' && find.trim() !== '' && shown.length > 0
          ? findCountWords(
            new Set(shown.flatMap((e) => (e.kind === 'booking' ? [e.row.booking_id] : []))).size,
            shown.filter((e) => e.kind === 'waiting').length,
          )
          : ''}
      </p>
    </>
  );
}

/** What Find found, said once to a screen reader: "1 booking", "12 bookings" (distinct
 *  bookings, so a return trip's two legs are one, board 3021), and on From today and
 *  Tomorrow its waiting requests too: "1 booking and 1 waiting request", "2 waiting
 *  requests". Empty when nothing was found (the empty state speaks then). */
export function findCountWords(bookings: number, waiting = 0): string {
  const count = (n: number, one: string, many: string): string => (n === 1 ? `1 ${one}` : `${String(n)} ${many}`);
  const parts = [
    bookings > 0 ? count(bookings, 'booking', 'bookings') : null,
    waiting > 0 ? count(waiting, 'waiting request', 'waiting requests') : null,
  ].filter((p): p is string => p !== null);
  return parts.join(' and ');
}

export { BookingRow };

export type BookingsProps = {
  registry: Registry;
  /** ⭐ board 2812 M5 (fix round 1, W2): `at` is the Who and When the row was found
   *  under; App hands it back as `initialFilter` when Back returns here. */
  onOpen: (bookingId: number, at: BookingsLanding) => void;
  /**
   * ⭐ WHERE THIS SCREEN OPENS when something else on the tool has asked: Done's
   * `Back to Bookings` asks for `BACK_TO_BOOKINGS` (Web, From today). It SEEDS the
   * filters once, at mount; every later tap is the operator's.
   */
  initialFilter?: BookingsLanding;
  /** Cleared by the SCREEN once it has read `initialFilter`, so a later visit
   *  opens on the ordinary default (`NursingHomes`'s `onOpened`, same reason). */
  onOpened?: () => void;
  /** A waiting row (From today, Tomorrow) opens Nursing homes' To do with that card open. */
  onOpenWaiting?: (requestId: number) => void;
};

/**
 * ⭐ board 2812 M4 — FIND, AS ONE PART: the visible label, the field and its debounce
 * (board 2812 M1 carry: one read per pause in typing), for Bookings' title row and a
 * home's Bookings tab. ⛔ The term it reports travels in Find's POST BODY
 * (`listBookingsFiltered`, Captain's SR1 ruling), never in a URL.
 */
export function FindField({ onFind }: { onFind: (term: string) => void }) {
  const [typed, setTyped] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  /** ⭐ board 2812 M4 fix round (C5): the term is handed on TRIMMED (the read trims it
   *  too), so "Aoife" then "Aoife " is the SAME state and no second read: a host's
   *  `useState` keeps an identical value without re-rendering. */
  const send = (value: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    onFind(value.trim());
  };
  return (
    <label class="bookings-find">
      <span class="bookings-find-label">Find</span>
      <input
        class="login-input bookings-search"
        type="search"
        placeholder="Name, resident, phone or reference"
        value={typed}
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value;
          setTyped(value);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => send(value), FIND_DEBOUNCE_MS);
        }}
        /* ⭐ board 2812 M4 fix round (C5): Enter finds NOW and cancels the pending pause,
           so a typed term followed by Enter is one read, not two. */
        onKeyDown={(e) => {
          if (e.key === 'Enter') send((e.target as HTMLInputElement).value);
        }}
      />
    </label>
  );
}

export function Bookings({ registry, onOpen, initialFilter, onOpened, onOpenWaiting }: BookingsProps) {
  // Read ONCE: useState's initial value is read on the first render only.
  const [landing] = useState<BookingsLanding>(initialFilter ?? BOOKINGS_DEFAULT);
  const [find, setFind] = useState('');

  useEffect(() => {
    onOpened?.();
  }, []);

  return (
    <div class="screen-pane screen-pane--wide">
      <div class="book-list-head">
        <h1 class="screen-title" tabIndex={-1}>Bookings</h1>
        {/* ⭐ FIND IN THE TITLE ROW, WITH A VISIBLE LABEL (plan step 6): one row of
            height, so at least five rows show at 1024x768 under bug-h. ⭐ board 2812 M4
            (SR1): the term travels in Find's POST BODY, never in a URL (S2). */}
        <FindField onFind={setFind} />
      </div>
      <BookingsList
        registry={registry}
        onOpen={onOpen}
        idPrefix="bookings"
        landing={landing}
        find={find}
        onOpenWaiting={onOpenWaiting}
      />
    </div>
  );
}
